import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, LockKeyhole, ArrowLeft } from "lucide-react";
import Seo from "@/components/Seo";
import Logo from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

const AdminLogin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-[70vh] items-center justify-center px-5 py-16" data-testid="admin-login-page">
      <Seo title="Admin Sign In | Solaris Transport LTD" description="Secure sign in for Solaris Transport LTD operations staff." path="/admin/login" />
      <div className="w-full max-w-md">
        <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-xl shadow-blue-900/5 sm:p-10">
          <Logo />
          <h1 className="mt-8 font-display text-2xl font-bold tracking-tight text-slate-900">Operations sign in</h1>
          <p className="mt-2 text-sm text-slate-500">Manage shipments and tracking updates.</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="admin-login-form">
            <label className="block">
              <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500">Email</span>
              <input
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm outline-none transition-colors focus:border-blue-600 focus:ring-2 focus:ring-blue-100"
                placeholder="you@solaris-transport-ltd.co.uk"
                data-testid="admin-login-email-input"
              />
            </label>
            <label className="block">
              <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500">Password</span>
              <input
                type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm outline-none transition-colors focus:border-blue-600 focus:ring-2 focus:ring-blue-100"
                placeholder="••••••••"
                data-testid="admin-login-password-input"
              />
            </label>

            {error && (
              <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700" data-testid="admin-login-error">
                {error}
              </p>
            )}

            <button
              type="submit" disabled={loading}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-blue-700 px-7 py-3.5 text-base font-bold text-white transition-colors hover:bg-blue-800 disabled:opacity-60"
              data-testid="admin-login-submit-button"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <LockKeyhole className="h-4 w-4" />}
              Sign in
            </button>
          </form>
        </div>
        <Link to="/" className="mt-6 flex items-center justify-center gap-2 text-sm font-semibold text-slate-500 hover:text-blue-700" data-testid="admin-login-back-link">
          <ArrowLeft className="h-4 w-4" /> Back to the website
        </Link>
      </div>
    </div>
  );
};

export default AdminLogin;
